'use client';

import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from 'react';
import {
	firebaseRemoteConfigService,
	REMOTE_CONFIG_KEYS,
} from '../api/remote-config-service';
import { useFirebase } from '@/core/firebase';
import { logger } from '@/core/logger';

interface IRemoteConfigContext {
	isLoaded: boolean;
	error: string | null;
	getBoolean: (key: string) => boolean;
	isTeamManagementEnabled: () => boolean;
	isProjectManagementEnabled: () => boolean;
	isPaymentEnabled: () => boolean;
	isByomEnabled: () => boolean;
	isSeedance2Enabled: () => boolean;
	isBillingTransparencyEnabled: () => boolean;
	isSceneBuilderEnabled: () => boolean;
	isCharacterRegistrationEnabled: () => boolean;
	is4kEnabled: () => boolean;
	isVoiceReferenceEnabled: () => boolean;
	isInteractiveEditingEnabled: () => boolean;
	isLayerDecompositionEnabled: () => boolean;
}

const RemoteConfigContext = createContext<IRemoteConfigContext | undefined>(
	undefined
);

export interface RemoteConfigProviderProps {
	children: ReactNode;
}

export function RemoteConfigProvider({ children }: RemoteConfigProviderProps) {
	const { app } = useFirebase();
	const [isLoaded, setIsLoaded] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!app) {
			// Firebase is still initializing
			return;
		}

		let mounted = true;

		const initializeRemoteConfig = async () => {
			try {
				// Initialize Remote Config with the app
				const success =
					await firebaseRemoteConfigService.initialize(app);
				if (!success) {
					logger.warn('Remote Config initialization failed');
				}

				if (mounted) {
					setIsLoaded(true);
					setError(null);
				}
			} catch (err) {
				logger.error('Remote Config initialization error', err);
				if (mounted) {
					setError(
						err instanceof Error
							? err.message
							: 'Remote Config initialization failed'
					);
					setIsLoaded(true); // Still mark as loaded to prevent infinite loading
				}
			}
		};

		initializeRemoteConfig();

		return () => {
			mounted = false;
		};
	}, [app]);

	const value: IRemoteConfigContext = {
		isLoaded,
		error,
		getBoolean: (key: string) =>
			firebaseRemoteConfigService.getBoolean(key),
		isTeamManagementEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_TEAM_MANAGEMENT_ENABLED
			),
		isProjectManagementEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_PROJECT_MANAGEMENT_ENABLED
			),
		isPaymentEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_PAYMENT_ENABLED
			),
		isByomEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_BYOM_ENABLED
			),
		isSeedance2Enabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_SEEDANCE_2_ENABLED
			),
		isBillingTransparencyEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_BILLING_TRANSPARENCY_ENABLED
			),
		isSceneBuilderEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_SCENE_BUILDER_ENABLED
			),
		isCharacterRegistrationEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_CHARACTER_REGISTRATION_ENABLED
			),
		is4kEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_4K_ENABLED
			),
		isVoiceReferenceEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_VOICE_REFERENCE_ENABLED
			),
		isInteractiveEditingEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_INTERACTIVE_EDITING_ENABLED
			),
		isLayerDecompositionEnabled: () =>
			firebaseRemoteConfigService.getBoolean(
				REMOTE_CONFIG_KEYS.IS_LAYER_DECOMPOSITION_ENABLED
			),
	};

	return (
		<RemoteConfigContext.Provider value={value}>
			{children}
		</RemoteConfigContext.Provider>
	);
}

export function useRemoteConfig(): IRemoteConfigContext {
	const context = useContext(RemoteConfigContext);

	if (!context) {
		throw new Error(
			'useRemoteConfig must be used within a RemoteConfigProvider'
		);
	}

	return context;
}
